import {Component, Input} from '@angular/core';

export interface DonneesDataTableColumn {
  key: string;
  label: string;
  width?: string;
}

const OMIT_JSON_DISPLAY_KEYS = new Set([
  'display_json',
  'api_json',
  'usage_json',
  'id',
  'table_db_id'
]);


@Component({
  selector: 'app-parametre-donnees-data-table',
  templateUrl: './parametre-donnees-data-table.component.html',
  styleUrls: ['./parametre-donnees-data-table.component.scss']
})
export class ParametreDonneesDataTableComponent {
  @Input() columns: DonneesDataTableColumn[] = [];
  @Input() rows: Record<string, unknown>[] = [];
  @Input() emptyMessage = 'Aucune donnée.';
  @Input() busy = false;
  @Input() jsonPrettyKeys: string[] = [];

  trackByIdx(index: number): number {
    return index;
  }

  isJsonPrettyKey(key: string): boolean {
    return this.jsonPrettyKeys.includes(key);
  }

  cellText(row: Record<string, unknown>, key: string): string {
    const v = row[key];
    let s = v === null || v === undefined ? '' : String(v);
    if (this.jsonPrettyKeys.includes(key)) {
      s = this.prettyJsonString(s);
    }
    return s;
  }

  private prettyJsonString(s: string): string {
    const t = s.trim();
    if (!t) {
      return s;
    }
    try {
      const parsed = JSON.parse(t);
      let cleaned = this.stripNullValues(parsed);
      if (cleaned === undefined) {
        return '';
      }
      cleaned = this.omitJsonKeysDeep(cleaned, OMIT_JSON_DISPLAY_KEYS);
      if (cleaned === undefined) {
        return '';
      }
      cleaned = this.stripEmptyObjectsDeep(cleaned);
      if (cleaned === undefined) {
        return '';
      }
      return JSON.stringify(cleaned, null, 2);
    } catch {
      return s;
    }
  }

  private stripNullValues(value: unknown): unknown {
    if (value === null) {
      return undefined;
    }
    if (Array.isArray(value)) {
      return value
        .map((item) => this.stripNullValues(item))
        .filter((item) => item !== undefined);
    }
    if (typeof value === 'object' && value !== null) {
      const o = value as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(o)) {
        const v = this.stripNullValues(o[k]);
        if (v !== undefined) {
          out[k] = v;
        }
      }
      return out;
    }
    return value;
  }

  private omitJsonKeysDeep(value: unknown, keys: ReadonlySet<string>): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => this.omitJsonKeysDeep(item, keys));
    }
    if (typeof value === 'object' && value !== null) {
      const o = value as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(o)) {
        if (keys.has(k)) {
          continue;
        }
        out[k] = this.omitJsonKeysDeep(o[k], keys);
      }
      return out;
    }
    return value;
  }

  private stripEmptyObjectsDeep(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value
        .map((item) => this.stripEmptyObjectsDeep(item))
        .filter((item) => item !== undefined && !this.isEmptyPlainObject(item));
    }
    if (typeof value === 'object' && value !== null) {
      const o = value as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(o)) {
        const v = this.stripEmptyObjectsDeep(o[k]);
        if (v === undefined || this.isEmptyPlainObject(v)) {
          continue;
        }
        out[k] = v;
      }
      return Object.keys(out).length === 0 ? undefined : out;
    }
    return value;
  }

  private isEmptyPlainObject(value: unknown): boolean {
    return (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      Object.keys(value as Record<string, unknown>).length === 0
    );
  }
}
